import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Option } from 'types';

import { useAppSelector } from 'redux/hooks';

import { customersDataService } from 'services/customers.service';
import { ordersDataService } from 'services/orders.service';

import { routes } from 'data/routes';

import { EditForm, FormItem } from 'sections/EditForm';
import { SectionType, TabWrap } from 'sections/TabWrap';

import { TabType } from 'components/Tabs';

import { internalWarehouses } from '../constants/warehouses';

export const useNewOrder = () => {
  const [customerOptions, setCustomerOptions] = useState<Option[]>([]);
  const [helperText, setHelperText] = useState<string>(
    'Customers are filtered by selected zone'
  );
  const seasonsData: any = useAppSelector((state) => state.constants.seasons);
  const zones = useAppSelector((state) => state.constants.zones);
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm({ mode: 'onChange' });
  const selectedZone = watch('zone');
  const navigate = useNavigate();

  const defineHelperText = (options: any) => {
    if (!options.length) {
      setHelperText('No customers found for this zone');
    } else {
      setHelperText(`Found ${options.length} customers for this zone`);
    }
  };

  const getOptions = async () => {
    try {
      const customersFromServer = await customersDataService.getOrderCustomers(selectedZone);
      const options = customersFromServer.data.content.map(({ code, companyName }: any) => ({
        value: code,
        label: `${code} - ${companyName}`,
      }));
      setCustomerOptions(options);
      defineHelperText(options);
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit = async ({ season, zone, warehouseInternalCode, customerCode }: any) => {
    try {
      await ordersDataService.post(
        season,
        zone,
        warehouseInternalCode === '_' ? '' : warehouseInternalCode,
        customerCode
      );
      toast.success('Order created successfully');
      reset();
      setCustomerOptions([]);
      setHelperText('Customers are filtered by selected zone');
      navigate(routes.orders);
    } catch (error) {
      toast.error('Error creating order');
      console.error(error);
    }
  };

  const addNewOrderFormData: FormItem[] = [
    {
      name: 'season',
      label: 'Season',
      type: 'select',
      options: seasonsData.map(({ season }: any) => ({ value: season, label: season })),
      required: true,
    },
    {
      name: 'zone',
      label: 'Zone',
      type: 'select',
      options: zones.map(({ code }: any) => ({ value: code, label: code })),
      required: true,
    },
    {
      name: 'warehouseInternalCode',
      label: 'Warehouse',
      type: 'select',
      options: internalWarehouses.map(({ code, label }: any) => ({
        value: code,
        label,
      })),
      required: true,
    },
    {
      name: 'customerCode',
      label: 'Customer',
      type: 'select',
      options: customerOptions,
      required: true,
      helperText,
    },
  ];

  const sections: SectionType[] = [
    {
      id: 1,
      content: (
        <EditForm
          onSubmit={handleSubmit(onSubmit)}
          data={addNewOrderFormData}
          register={register}
          control={control}
          hideUndo
          hasButtons
          submitBtnLabel="Submit"
          isButtonsDisabled={!isValid || isSubmitting}
          isDirty={isDirty}
          errors={errors}
        />
      ),
    },
  ];

  const addNewOrderForm = <TabWrap sections={sections} />;

  const newOrdersTabs: TabType[] = [
    {
      id: 1,
      title: '',
      content: addNewOrderForm,
    },
  ];

  useEffect(() => {
    if (selectedZone !== undefined) {
      getOptions();
    }
  }, [selectedZone]);

  return {
    newOrdersTabs,
  };
};
